/**
 * MeasurementEquipment Types for MEV-Med Frontend
 * Type definitions for measurement equipment management
 *
 * @version 2025.1.0
 * @description Tipos TypeScript para gestão de equipamentos de medição
 */

import { RiskType } from './occupationalRisk';

/**
 * Enum for MeasurementEquipment status
 */
export enum MeasurementEquipmentStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE'
}

/**
 * Enum for validity status (calculated based on days until expiry)
 */
export enum ValidityStatus {
  VALID = 'VALID',       // Green - More than 30 days until expiry
  WARNING = 'WARNING',   // Yellow - 30 days or less until expiry
  EXPIRED = 'EXPIRED'    // Red - Past expiry date
}

/**
 * Labels for validity status display
 */
export const ValidityStatusLabels: Record<ValidityStatus, string> = {
  [ValidityStatus.VALID]: 'Válido',
  [ValidityStatus.WARNING]: 'Expirando',
  [ValidityStatus.EXPIRED]: 'Expirado'
};

/**
 * Colors for validity status display
 */
export const ValidityStatusColors: Record<ValidityStatus, 'success' | 'warning' | 'error'> = {
  [ValidityStatus.VALID]: 'success',
  [ValidityStatus.WARNING]: 'warning',
  [ValidityStatus.EXPIRED]: 'error'
};

/**
 * MeasurementEquipment entity interface
 */
export interface MeasurementEquipment {
  id: number;
  companyId?: number;
  companyName?: string;
  isGlobal: boolean;
  name: string;
  serialNumber?: string;
  manufacturer: string;
  model: string;
  occupationalRisks: Array<{
    id: number;
    riskFactorName: string;
    riskType: RiskType;
  }>;
  eSocialCode?: string;
  calibrationDate: string;
  validityDate: string;
  methodology: string;
  detectsHarmfulAgents: boolean;
  status: MeasurementEquipmentStatus;
  validityStatus: ValidityStatus;
  daysUntilExpiry: number;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
}

/**
 * Filters for listing measurement equipment
 */
export interface MeasurementEquipmentFilters {
  search?: string;
  companyId?: number;
  isGlobal?: boolean;
  occupationalRiskIds?: number[];
  validityStatus?: ValidityStatus;
  status?: MeasurementEquipmentStatus;
  includeGlobal?: boolean;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

/**
 * Form data for creating/updating measurement equipment
 */
export interface MeasurementEquipmentFormData {
  companyId?: number;
  isGlobal: boolean;
  name: string;
  serialNumber?: string;
  manufacturer: string;
  model: string;
  occupationalRiskIds: number[];
  eSocialCode?: string;
  calibrationDate: string;
  validityDate: string;
  methodology: string;
  detectsHarmfulAgents: boolean;
  status: MeasurementEquipmentStatus;
}

/**
 * API payload for creating/updating measurement equipment
 */
export interface MeasurementEquipmentApiPayload {
  companyId?: number;
  isGlobal: boolean;
  name: string;
  serialNumber?: string;
  manufacturer: string;
  model: string;
  occupationalRiskIds?: number[];
  eSocialCode?: string;
  calibrationDate: string;
  validityDate: string;
  methodology: string;
  detectsHarmfulAgents?: boolean;
  status?: MeasurementEquipmentStatus;
}

/**
 * Response for list endpoint with pagination
 */
export interface MeasurementEquipmentListResponse {
  equipments: MeasurementEquipment[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Props for UI components
 */
export interface MeasurementEquipmentTableProps {
  equipments: MeasurementEquipment[];
  onEdit?: (equipment: MeasurementEquipment) => void;
  onDelete?: (id: number) => void;
  onView?: (equipment: MeasurementEquipment) => void;
  loading?: boolean;
}

export interface MeasurementEquipmentFormProps {
  initialData?: MeasurementEquipment;
  onSubmit: (data: MeasurementEquipmentFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  companies?: Array<{ id: number; name: string }>;
  occupationalRisks?: Array<{ id: number; riskFactorName: string; riskType: RiskType }>;
}

export interface MeasurementEquipmentFiltersProps {
  filters: MeasurementEquipmentFilters;
  onChange: (filters: MeasurementEquipmentFilters) => void;
  companies?: Array<{ id: number; name: string }>;
}

export interface ValidityStatusChipProps {
  status: ValidityStatus;
  daysUntilExpiry?: number;
  size?: 'small' | 'medium';
}
